import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, Loader2, Sparkles } from "lucide-react";
import { cn } from "@/lib/cn";
import { listPublicPlans, type PricingPlan } from "@/lib/console-api";

function formatPrice(plan: PricingPlan): string {
  if (plan.priceCents === 0) return "무료";
  const amount = (plan.priceCents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: plan.currency || "USD",
    maximumFractionDigits: 0,
  });
  const period = plan.billingPeriod === "year" ? "년" : "월";
  const seat = plan.isPerSeat ? " · 1명당" : "";
  return `${amount}${seat} / ${period}`;
}

export function PricingPage(): JSX.Element {
  const plansQuery = useQuery({
    queryKey: ["pricing-plans-public"],
    queryFn: listPublicPlans,
  });

  const plans = plansQuery.data ?? [];

  return (
    <main className="container py-20">
      <div className="mx-auto max-w-2xl text-center">
        <p className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/60 px-3 py-1 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" /> 가격
        </p>
        <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
          시작은 무료, 팀이 늘면 함께
        </h1>
        <p className="mt-4 text-base text-muted-foreground">
          모든 플랜에서 데스크톱 앱의 모든 기능을 쓸 수 있습니다. 코드와 키는 항상
          내 컴퓨터에만 머무릅니다.
        </p>
      </div>

      {plansQuery.isLoading && (
        <p className="mt-12 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> 가격 정보를 불러오는 중…
        </p>
      )}

      {!plansQuery.isLoading && plans.length === 0 && (
        <div className="mx-auto mt-12 max-w-md rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          가격 플랜이 아직 등록되지 않았습니다.
        </div>
      )}

      {!plansQuery.isLoading && plans.length > 0 && (
        <div
          className={cn(
            "mx-auto mt-16 grid max-w-5xl gap-6",
            plans.length === 1
              ? "max-w-md grid-cols-1"
              : plans.length === 2
                ? "max-w-3xl grid-cols-1 sm:grid-cols-2"
                : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
          )}
        >
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      )}

      <p className="mt-16 text-center text-xs text-muted-foreground">
        지금은 모든 플랜을 무료로 쓸 수 있습니다. 유료로 바뀔 때는 미리 알려 드리며, 직접 결제하기
        전에는 요금이 청구되지 않습니다.
      </p>
    </main>
  );
}

function PlanCard({ plan }: { plan: PricingPlan }): JSX.Element {
  const isFree = plan.priceCents === 0;
  return (
    <article
      className={cn(
        "relative flex flex-col rounded-2xl border p-6 shadow-sm",
        plan.highlighted
          ? "border-primary bg-primary/5"
          : "border-border bg-card",
      )}
    >
      {plan.highlighted && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
          추천
        </span>
      )}
      <h2 className="text-lg font-semibold">{plan.name}</h2>
      {plan.description && (
        <p className="mt-1 text-sm text-muted-foreground">{plan.description}</p>
      )}

      <div className="mt-5">
        {isFree ? (
          <p className="text-3xl font-bold tracking-tight">무료</p>
        ) : (
          <p className="text-3xl font-bold tracking-tight">
            {formatPrice(plan)}
          </p>
        )}
      </div>

      <ul className="mt-6 flex-1 space-y-2.5 text-sm">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check
              className="mt-0.5 h-4 w-4 shrink-0 text-primary"
              aria-hidden
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Link
        to={plan.slug === "enterprise" ? "/contact" : "/signup"}
        className={cn(
          "mt-8 inline-flex h-10 items-center justify-center rounded-md text-sm font-medium transition-colors",
          plan.highlighted
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        )}
      >
        {plan.slug === "enterprise" ? "문의하기" : isFree ? "무료로 시작" : "시작하기"}
      </Link>
    </article>
  );
}
